import { ChangeEvent, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { BaseSchema } from 'yup';

import { FormContext } from './FormContext';
import { FormFieldsContext } from './FormFields';
import { ValidationError } from './validation-error';

export type FieldWidth = 12.5 | 25 | 33 | 37.5 | 50 | 75 | 100;
export type FieldEvent<T = any> = (
  value: T,
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void,
) => void;

type FieldEvents<T = any> = {
  onFocus: FieldEvent<T>;
  onChange: FieldEvent<T>;
  onBlur: FieldEvent<T>;
};

interface IUseFormField<T> {
  id: string;
  hasError: boolean;
  error: ValidationError;
  required: boolean;
  disabled: boolean;
  isFocused: boolean;
  value: T;
  inputProps: any;
  fieldProps: {
    className: string;
  };
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
}

const getFormikProperty = (obj, property) =>
  property.split('.').reduce((obj, property) => obj && obj[property], obj);

const getFieldValidationSchema = (obj, property): BaseSchema =>
  property.split('.').reduce((obj, property) => obj && obj.fields[property], obj);

function useFormField<T = any>(
  name: string,
  width: FieldWidth = 50,
  { onFocus, onChange, onBlur }: Partial<FieldEvents<T>> = {},
  autoFocus: boolean = false,
): IUseFormField<T> {
  const formContext = useContext(FormContext);
  const fieldsContext = useContext(FormFieldsContext);

  const [isFocused, setIsFocused] = useState<boolean>();
  const error = getFormikProperty(formContext.formik.errors, name);
  const hasError = Boolean(getFormikProperty(formContext.formik.touched, name) && error);

  const required = useMemo(() => {
    const fieldValidationSchema = getFieldValidationSchema(formContext.validationSchema, name);
    const filedValidationMetadata = fieldValidationSchema?.meta();

    return (
      fieldValidationSchema?.spec.presence === 'required' ||
      Boolean(filedValidationMetadata && filedValidationMetadata['required'])
    );
  }, [formContext.validationSchema]);

  const eventHandler = (value: any, callback: FieldEvent<T>) => {
    if (callback) {
      callback(value, formContext.formik.setFieldValue);
    }
  };

  const currentValue = getFormikProperty(formContext.values, name);
  const fieldClassName = classNames(
    'novoFormField',
    `novoFormField--width${width.toString().replace('.', '-')}`,
    {
      'novoFormField--error': hasError,
    },
  );

  return {
    id: `${name}-${formContext.id}`,
    hasError,
    error,
    required,
    disabled: formContext.disabled || fieldsContext?.disabled,
    isFocused,
    value: currentValue,
    fieldProps: {
      className: fieldClassName,
    },
    inputProps: {
      ...formContext.formik.getFieldProps(name),
      autoFocus,
      onFocus: () => {
        setIsFocused(true);
        eventHandler(currentValue, onFocus);
      },
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        formContext.formik.getFieldProps(name).onChange(event);
        eventHandler(event.target.value, onChange);
      },
      onBlur: (event: ChangeEvent<HTMLInputElement>) => {
        formContext.formik.getFieldProps(name).onBlur(event);
        eventHandler(currentValue, onBlur);
        setIsFocused(false);
      },
    },
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => {
      formContext.formik.setFieldValue(field, value, shouldValidate);
      eventHandler(value, onChange);
    },
    setFieldTouched: (field: string, value: any, shouldValidate?: boolean) => {
      formContext.formik.setFieldTouched(field, value, shouldValidate);
      eventHandler(currentValue, onBlur);
    },
  };
}

export default useFormField;
